import React from 'react';
import { Columns, Check } from 'lucide-react';

interface ColumnMapperProps {
  schema: TableSchema;
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  imageColumn: string | null;
  onImageColumnChange: (column: string | null) => void;
  mediaType: MediaType;
}

interface TableSchema {
  columns: Array<{
    name: string;
    type: string;
  }>;
}

const ColumnMapper: React.FC<ColumnMapperProps> = ({ 
  schema, 
  selectedColumns, 
  onColumnsChange,
  imageColumn,
  onImageColumnChange,
  mediaType
}) => {
  const handleToggleColumn = (columnName: string) => {
    if (selectedColumns.includes(columnName)) {
      onColumnsChange(selectedColumns.filter(c => c !== columnName));
    } else {
      onColumnsChange([...selectedColumns, columnName]);
    }
  };

  const handleSelectAll = () => {
    if (selectedColumns.length === schema.columns.length) {
      onColumnsChange([]);
    } else {
      onColumnsChange(schema.columns.map(c => c.name));
    }
  };

  const mediaBorderColor = mediaType === 'image' ? 'purple' : 'blue';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Columns className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-semibold text-white">Select Columns</h2>
        </div>
        <button
          onClick={handleSelectAll}
          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          {selectedColumns.length === schema.columns.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <p className="text-sm text-gray-400 mb-4">
        Choose which columns to use in your prompt template. The AI will use input images to generate {mediaType === 'image' ? 'new images' : 'videos'}.
      </p>

      {/* Image Column Selection - Always for input */}
      <div className={`bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-4`}>
        <label className={`block text-sm font-medium text-purple-300 mb-2`}>
          📷 Input Image Column (Required)
        </label>
        <p className="text-xs text-gray-400 mb-3">
          Select the column with input images - AI will use these to generate {mediaType === 'image' ? 'new images' : 'videos'}
        </p>
        <select
          value={imageColumn || ''}
          onChange={(e) => onImageColumnChange(e.target.value || null)}
          className={`w-full bg-gray-900/50 border border-purple-500/30 text-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition`}
        >
          <option value="">-- Select input image column --</option>
          {schema.columns.map((column) => (
            <option key={column.name} value={column.name}>
              {column.name} ({column.type})
            </option>
          ))}
        </select>
        {imageColumn && (
          <p className="text-xs text-green-400 mt-2">
            ✓ Input images will be loaded from: <span className="font-mono">{imageColumn}</span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto pr-2">
        {schema.columns.map((column) => {
          const isSelected = selectedColumns.includes(column.name);
          const isImageColumn = column.name === imageColumn;
          return (
            <button
              key={column.name}
              onClick={() => handleToggleColumn(column.name)}
              className={`
                flex items-center justify-between p-3 rounded-lg border transition-all
                ${isImageColumn
                  ? `bg-${mediaBorderColor}-500/20 border-${mediaBorderColor}-500/50 shadow-lg shadow-${mediaBorderColor}-500/20 opacity-50 cursor-not-allowed`
                  : isSelected 
                  ? 'bg-blue-500/20 border-blue-500/50 shadow-lg shadow-blue-500/20' 
                  : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                }
              `}
              disabled={isImageColumn}
            >
              <div className="flex-1 text-left">
                <div className="font-medium text-white text-sm flex items-center gap-2">
                  {column.name}
                  {isImageColumn && <span className={`text-purple-400 text-xs`}>📷 Input</span>}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{column.type}</div>
              </div>
              {isSelected && !isImageColumn && (
                <Check className="w-5 h-5 text-blue-400 flex-shrink-0 ml-2" />
              )}
            </button>
          );
        })}
      </div>

      {selectedColumns.length > 0 && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mt-4">
          <p className="text-sm text-green-300">
            <strong>{selectedColumns.length}</strong> column{selectedColumns.length !== 1 ? 's' : ''} selected for prompt variables
            {imageColumn && <span className="ml-2">+ <strong>1</strong> input image column</span>}
          </p>
        </div>
      )}
    </div>
  );
};

export default ColumnMapper;
